import React, { useState, useEffect, useRef } from 'react';
import { Modal, Input, Button } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import axios from '../../utility/axios';

const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Reusable access code modal for recipient verification.
 * Used when MessageView, DocumentView, DocumensoView (or reply views) receive 401 requires_verification.
 * Works for both authenticated (SideMenuLayout) and unauthenticated (Layout) users.
 */
const AccessCodeModal = ({ open, rid, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const [accessCode, setAccessCode] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [error, setError] = useState('');
    const [resending, setResending] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const codeInputRef = useRef(null);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const handleVerify = async (e) => {
        e?.preventDefault();
        if (!rid || !accessCode.trim()) {
            setError(t('contactsMailbox.enterCode'));
            return;
        }
        setError('');
        setVerifying(true);
        try {
            await axios.post('/api/contacts-mailbox/verify/', {
                rid,
                access_code: accessCode.trim(),
            });
            onSuccess?.();
        } catch (err) {
            if (err.response?.status === 410 && err.response?.data?.expired) {
                setAccessCode('');
                setTimeout(() => codeInputRef.current?.focus(), 50);
            }
            setError(err.response?.data?.error || t('contactsMailbox.verifyFailed'));
        } finally {
            setVerifying(false);
        }
    };

    const handleResend = async () => {
        if (!rid || resendCooldown > 0) return;
        setResending(true);
        setError('');
        try {
            await axios.post('/api/contacts-mailbox/resend/', { rid });
            setResendCooldown(RESEND_COOLDOWN_SECONDS);
            setAccessCode('');
            setTimeout(() => codeInputRef.current?.focus(), 50);
        } catch (err) {
            setError(err.response?.data?.error || t('contactsMailbox.resendFailed'));
        } finally {
            setResending(false);
        }
    };

    const handleClose = () => {
        setAccessCode('');
        setError('');
        setResendCooldown(0);
        onCancel?.();
    };

    return (
        <Modal
            title={
                <span>
                    <LockOutlined style={{ marginRight: 8 }} />
                    {t('contactsMailbox.enterAccessCode')}
                </span>
            }
            open={open}
            onCancel={handleClose}
            footer={null}
            destroyOnClose
        >
            <p style={{ color: '#666', marginBottom: 16 }}>
                {t('contactsMailbox.accessCodeHint')}
            </p>
            <form onSubmit={handleVerify}>
                <Input
                    ref={codeInputRef}
                    placeholder={t('contactsMailbox.accessCode')}
                    value={accessCode}
                    onChange={(e) => {
                        setAccessCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                        setError('');
                    }}
                    maxLength={6}
                    size="large"
                    disabled={verifying}
                    autoFocus
                    status={error ? 'error' : ''}
                    style={{ marginBottom: error ? 6 : 12 }}
                />
                {error && (
                    <div style={{ color: '#ff4d4f', fontSize: 13, marginBottom: 12 }}>
                        {error}
                    </div>
                )}
                <Button type="primary" htmlType="submit" loading={verifying} size="large" block>
                    {t('contactsMailbox.verify')}
                </Button>
                <Button
                    type="link"
                    onClick={handleResend}
                    loading={resending}
                    disabled={resendCooldown > 0}
                    block
                    style={{ marginTop: 8 }}
                >
                    {resendCooldown > 0
                        ? t('contactsMailbox.resendIn', { seconds: resendCooldown })
                        : t('contactsMailbox.resendCode')}
                </Button>
            </form>
        </Modal>
    );
};

export default AccessCodeModal;
